import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Stack,
  useTheme,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import { alpha } from '@mui/material/styles';
import { useNavbar } from "../context/NavbarContext";
import { fetchActivityLogs } from "../api/apiService";
import { format } from "date-fns";

const AdminLogs = () => {
  const { isNavbarOpen } = useNavbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [logs, setLogs] = useState([]);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [loadingLogs, setLoadingLogs] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoadingLogs(true);
        const items = await fetchActivityLogs(selectedDate);
        const logsData = items.map((item) => ({
          id: `${item.userEmail}-${item.timestamp}`,
          userEmail: item.userEmail,
          action: item.action,
          timestamp: item.timestamp,
        }));
        setLogs(logsData);
      } catch (err) {
        console.error("Error fetching activity logs:", err);
      } finally {
        setLoadingLogs(false);
      }
    };

    fetchLogs();
  }, [selectedDate]);

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh', 
      width: '100%', 
      background: (theme) => theme.palette.background.default,
      margin: 0,
      padding: 0,
      overflow: 'auto',
    }}>
      <Container
        disableGutters
        sx={{
          minHeight: '100vh',
          width: '100%',
          flexGrow: 1,
          padding: {
            xs: '12px',
            sm: '16px',
            md: '20px',
            lg: '24px',
            xl: '32px'
          },
          marginLeft: {
            xs: isNavbarOpen ? '240px' : '70px',
            sm: isNavbarOpen ? '240px' : '70px',
            md: isNavbarOpen ? '240px' : '70px',
            lg: isNavbarOpen ? '240px' : '70px',
            xl: isNavbarOpen ? '240px' : '70px'
          },
          marginTop: {
            xs: '65px',
            sm: '64px',
            md: '64px',
            lg: '64px',
            xl: '64px'
          },
          transition: 'margin-left 0.3s, padding 0.3s',
          position: 'relative',
          maxWidth: '100% !important',
          overflow: 'auto',
        }}
      >
        {/* Page Header */}
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          justifyContent="space-between" 
          sx={{ 
            marginBottom: {
              xs: 2,
              sm: 3,
              md: 3
            }, 
            marginTop: {
              xs: 1,
              sm: 2,
              md: 2
            },
            gap: {
              xs: 2,
              sm: 0
            }
          }}
        >
          <Typography 
            variant="h4" 
            fontWeight="600"
            sx={{ 
              color: (theme) => theme.palette.text.primary,
              letterSpacing: '-0.5px',
              position: 'relative',
              fontSize: {
                xs: '1.5rem',
                sm: '1.75rem',
                md: '2rem',
                lg: '2.25rem',
                xl: '2.5rem'
              },
            }}
          >
            Activity Logs
          </Typography>
          <Typography variant="body2" sx={{ color: (theme) => theme.palette.text.secondary, display: { xs: 'block', sm: 'none' } }}>
            View user actions by date
          </Typography>
          <TextField
            label="Select Date"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            size={isMobile ? "small" : "medium"}
            aria-label="Select date for activity logs"
            sx={{ 
              backgroundColor: (theme) => theme.palette.background.paper,
              borderRadius: "8px",
              '& .MuiOutlinedInput-notchedOutline': { borderColor: (theme) => theme.palette.divider },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: (theme) => theme.palette.primary.main },
              width: {
                xs: '100%',
                sm: 'auto'
              },
              '& .MuiInputLabel-root': {
                fontSize: {
                  xs: '0.75rem',
                  sm: '0.875rem',
                  md: '1rem'
                }
              },
              '& .MuiInputBase-input': {
                fontSize: {
                  xs: '0.75rem',
                  sm: '0.875rem',
                  md: '1rem'
                }
              }
            }}
          />
        </Stack>

        {/* Table Section */}
        <TableContainer 
          component={Paper} 
          elevation={2} 
          sx={{ 
            width: '100%', 
            borderRadius: "12px",
            overflow: 'hidden', 
            boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 6px 18px rgba(0,0,0,0.35)' : '0 4px 10px rgba(0,0,0,0.05)',
            backgroundColor: (theme) => theme.palette.background.paper,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            overflowX: 'auto'
          }}
        >
          {loadingLogs ? (
            <Box sx={{ p: 2 }}>
              <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
              {[...Array(6)].map((_, idx) => (
                <Skeleton key={idx} variant="rectangular" height={isMobile ? 36 : 52} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : (
          <Table 
            size={isMobile ? "small" : "medium"}
            sx={{ minWidth: 650 }}
            aria-label="Activity logs table"
          >
            <TableHead sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#F4F6F8' }}>
              <TableRow>
                <TableCell sx={{ 
                  fontWeight: "bold", 
                  fontSize: {
                    xs: '0.75rem',
                    sm: '0.8rem',
                    md: '0.875rem'
                  }, 
                  color: (theme) => theme.palette.text.secondary 
                }}>
                  User Email
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: "bold", 
                  fontSize: {
                    xs: '0.75rem',
                    sm: '0.8rem',
                    md: '0.875rem'
                  }, 
                  color: (theme) => theme.palette.text.secondary 
                }}>
                  Action
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: "bold", 
                  fontSize: {
                    xs: '0.75rem',
                    sm: '0.8rem',
                    md: '0.875rem'
                  }, 
                  color: (theme) => theme.palette.text.secondary 
                }}>
                  Timestamp
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length > 0 ? (
                logs.map((log) => (
                  <TableRow key={log.id} sx={{ '&:hover': { backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.06) } }}>
                    <TableCell sx={{ 
                      fontSize: {
                        xs: '0.75rem',
                        sm: '0.8rem',
                        md: '0.875rem'
                      }, 
                      color: (theme) => theme.palette.text.primary 
                    }}>
                      {log.userEmail}
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: {
                        xs: '0.75rem',
                        sm: '0.8rem',
                        md: '0.875rem'
                      }, 
                      color: (theme) => theme.palette.text.primary 
                    }}>
                      {log.action}
                    </TableCell>
                    <TableCell sx={{ 
                      fontSize: {
                        xs: '0.75rem',
                        sm: '0.8rem',
                        md: '0.875rem'
                      }, 
                      color: (theme) => theme.palette.text.primary 
                    }}>
                      {log.timestamp || "N/A"}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell 
                    colSpan={3} 
                    align="center" 
                    sx={{ 
                      color: (theme) => theme.palette.text.secondary, 
                      padding: {
                        xs: "16px",
                        sm: "20px",
                        md: "20px"
                      },
                      fontSize: {
                        xs: '0.75rem',
                        sm: '0.8rem',
                        md: '0.875rem'
                      }
                    }}
                  >
                    No logs available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          )}
        </TableContainer>
      </Container>
    </Box>
  );
};

export default AdminLogs;
